import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Users, 
  ArrowRight,
  Check,
  Globe,
  Building2,
  Plug,
  Briefcase,
  Headphones,
  BarChart3,
  Shield,
  Zap,
  Clock,
  Monitor,
  Smartphone,
  CheckCircle,
  Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Business() {
  useEffect(() => {
    document.title = "Connect | For Business";
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      icon: Monitor,
      title: 'No Hardware Required',
      description: 'Works entirely in your browser. No plugins, downloads, or special equipment — just a computer with a microphone.'
    },
    {
      icon: Plug,
      title: 'Works With Your PBX',
      description: 'Connect to your existing Cloud PBX provider. Keep your phone numbers, call flows, and settings.'
    },
    {
      icon: Clock,
      title: 'Up and Running Fast',
      description: 'Get started in minutes. Simply enter your PBX credentials and start making calls right away.'
    },
    {
      icon: Users,
      title: 'Team Visibility',
      description: 'See who\'s available, who\'s on a call, and transfer calls seamlessly across your team.'
    },
    {
      icon: BarChart3,
      title: 'Call Analytics',
      description: 'Understand your call patterns with detailed analytics. Track volumes, durations, and team performance.'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Encrypted calls, secure authentication, and 99.9% uptime. Your communications are protected.'
    }
  ];

  const useCases = [
    {
      title: 'Sales Teams',
      description: 'Make outbound calls, track call history, and never miss a follow-up.',
      features: ['Click-to-call from CRM', 'Call recordings', 'Activity tracking']
    },
    {
      title: 'Support Teams',
      description: 'Monitor queues, track wait times, and ensure great customer service.',
      features: ['Queue monitoring', 'Real-time presence', 'Call analytics']
    },
    {
      title: 'Remote Teams',
      description: 'Stay connected from anywhere with a consistent phone experience.',
      features: ['Works from any location', 'Mobile-ready', 'Team presence']
    }
  ];

  const integrations = [
    { name: 'Hero Internet', status: 'Available', description: 'NZ Cloud PBX' },
    { name: 'More Providers', status: 'Coming Soon', description: 'Additional integrations' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold text-white tracking-tight">CONNECT</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-slate-300 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-slate-300 hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="text-cyan-400 font-medium">For Business</Link>
            <Link to="/partners" className="text-slate-300 hover:text-white transition-colors">Partners</Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                Sign In
              </Button>
            </Link>
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/25">
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-8">
            <Briefcase className="w-4 h-4" />
            For Business
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
            A Modern Console
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              For Your Cloud PBX
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            Your team deserves better than a basic phone app. Get a full-featured operator console 
            that works with your existing Cloud PBX — all in your browser.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login?tab=register">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-6 text-lg shadow-xl shadow-cyan-500/25">
                Start Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/features">
              <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-6 text-lg">
                <Play className="w-5 h-5 mr-2" />
                See Features
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Businesses Choose CONNECT
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Get a modern phone experience without changing your phone system.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <div 
                key={i}
                className="group p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50 hover:border-cyan-500/30 hover:bg-slate-800/50 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center mb-4 group-hover:from-cyan-500/30 group-hover:to-blue-600/30 transition-all">
                  <benefit.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Integrations */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm mb-6">
              <Plug className="w-4 h-4" />
              Works With Your PBX
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Connects to Your Cloud PBX
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Keep your existing phone system. CONNECT works alongside your Cloud PBX provider.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 max-w-3xl mx-auto">
            {integrations.map((integration, i) => (
              <div 
                key={i}
                className={`flex-1 p-6 rounded-2xl border ${
                  integration.status === 'Available' 
                    ? 'bg-gradient-to-br from-slate-800/50 to-slate-900/50 border-slate-700/50' 
                    : 'bg-slate-800/20 border-slate-700/30 border-dashed'
                }`}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    integration.status === 'Available' 
                      ? 'bg-gradient-to-br from-orange-500/20 to-red-500/20' 
                      : 'bg-slate-700/30'
                  }`}>
                    <Globe className={`w-6 h-6 ${integration.status === 'Available' ? 'text-orange-400' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <h3 className={`font-bold ${integration.status === 'Available' ? 'text-white' : 'text-slate-400'}`}>
                      {integration.name}
                    </h3>
                    <p className={`text-sm ${integration.status === 'Available' ? 'text-slate-400' : 'text-slate-500'}`}>
                      {integration.description}
                    </p>
                  </div>
                  <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
                    integration.status === 'Available' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {integration.status}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            Don't see your provider? <Link to="/partners" className="text-cyan-400 hover:underline">Let us know</Link> and we'll prioritize it.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Perfect For Every Team
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Whether you're in sales, support, or working remotely — CONNECT helps your team communicate better.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, i) => (
              <div 
                key={i}
                className="p-8 rounded-2xl bg-gradient-to-b from-slate-800/50 to-slate-800/30 border border-slate-700/50"
              >
                <h3 className="text-xl font-semibold text-white mb-3">{useCase.title}</h3>
                <p className="text-slate-400 mb-6">{useCase.description}</p>
                <ul className="space-y-2">
                  {useCase.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-slate-400 mb-12">
            Start free with up to 2 users. Upgrade when you're ready.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {/* Free Tier */}
            <div className="p-8 rounded-2xl bg-slate-800/30 border border-slate-700/50">
              <h3 className="text-xl font-bold text-white mb-2">Free</h3>
              <p className="text-slate-400 text-sm mb-4">Perfect to get started</p>
              <div className="text-4xl font-bold text-white mb-4">$0</div>
              <ul className="space-y-3 mb-6 text-left">
                {['Up to 2 users', 'WebRTC softphone', '30-day call history', 'Basic presence'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-cyan-400" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/login?tab=register">
                <Button className="w-full" variant="outline">
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Paid Tier */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border-2 border-cyan-500/50">
              <h3 className="text-xl font-bold text-white mb-2">Pro</h3>
              <p className="text-slate-400 text-sm mb-4">For growing teams</p>
              <div className="flex items-baseline justify-center gap-1 mb-4">
                <span className="text-4xl font-bold text-white">$20</span>
                <span className="text-slate-400">/user/mo</span>
              </div>
              <ul className="space-y-3 mb-6 text-left">
                {['Unlimited users', 'Unlimited call history', 'Queue monitoring', 'Analytics dashboard', 'AI Transcripts', 'Global Contacts'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-cyan-400" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/login?tab=register">
                <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>

          <p className="text-slate-500 text-sm mt-6">
            <Link to="/pricing" className="text-cyan-400 hover:underline">View full pricing details</Link> for complete feature breakdown.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border-y border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Upgrade Your Phone Experience?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Start with our free tier. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login?tab=register">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg font-semibold">
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/features">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                Explore Features
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-bold">CONNECT</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/features" className="hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/business" className="hover:text-white transition-colors">For Business</Link>
            <Link to="/partners" className="hover:text-white transition-colors">Partners</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} Connect-US. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
